<template>
	<widget-settings-layout ref="layout" slug="gift" name="Alert" :error="saveError" :saving="saving" :has-unsaved-changes="hasUnsavedChanges" show-audio :default-width="1280" :settings="settings" :saved-settings-json="savedSettingsJson" @save="save">
		<template v-if="settings">
		<b-card class="mb-3">
			<b-form-row class="mb-3">
				<b-col>
					<b-form-checkbox switch inline v-model="settings.enableEventSounds" class="mb-2">Enable event sounds</b-form-checkbox>
					<b-form-group label="Alert minimum amount">
						<b-input-group :append="user?.currency_code">
							<b-form-input type="number" min="0" v-model="settings.minDonation"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-checkbox switch inline v-model="settings.enableTTS" class="mb-2">Enable text-to-speech</b-form-checkbox>
					<b-form-group label="TTS minimum amount">
						<b-input-group :append="user?.currency_code">
							<b-form-input type="number" min="0" v-model="settings.ttsMinDonation" :disabled="!settings.enableTTS"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-checkbox switch inline v-model="settings.enableMedia" class="mb-2">Enable media playback</b-form-checkbox>
					<p class="text-muted small mb-0">Time limits, minimum views and minimum donation are configured in <router-link to="/widgets/thresholds">Song request thresholds</router-link>.</p>
				</b-col>
			</b-form-row>
			<b-form-row class="mb-3">
				<b-col>
					<b-form-checkbox switch inline v-model="settings.enableTTSProlog" class="mb-2" :disabled="!settings.enableTTS">Read out sender name and gift value</b-form-checkbox>
				</b-col>
			</b-form-row>
			<volume-slider v-model="settings.volume" />
		</b-card>
		<b-card class="mb-3" title="Queue">
			<!-- Whether to pre-populate the queue with unplayed alerts from the DB on widget load -->
			<b-form-row class="mb-3">
				<b-col>
					<!-- Hidden = no strip; Above = strip appears above the active alert; Below = strip appears below -->
					<b-form-group label="Queue strip position" class="mb-0"
						description="Where to show waiting alerts">
						<b-form-select v-model="settings.queuePosition" :options="queuePositionOptions"></b-form-select>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-checkbox switch inline v-model="settings.loadQueue">Load alert queue at startup</b-form-checkbox>
				</b-col>
			</b-form-row>

			<!-- Queue strip: small avatar circles shown while alerts are waiting to play -->
			<b-form-row>
				<b-col>
					<!-- Cap how many circles are shown; extras appear as "+N" -->
					<b-form-group label="Max circles" class="mb-0"
						description="Extras shown as +N">
						<b-form-input type="number" min="1" max="20" v-model.number="settings.queueIconCount"
							:disabled="settings.queuePosition === 'hidden'"></b-form-input>
					</b-form-group>
				</b-col>
				<b-col>
					<!-- Diameter of each avatar circle in the strip -->
					<b-form-group label="Circle size" class="mb-0">
						<b-input-group append="px">
							<b-form-input type="number" min="8" max="200" v-model.number="settings.queueIconSize"
								:disabled="settings.queuePosition === 'hidden'"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>

		<b-card class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="In Transition">
						<b-form-select v-model="settings.inTransition" :options="inTransitions"></b-form-select>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Out Transition">
						<b-form-select v-model="settings.outTransition" :options="outTransitions"></b-form-select>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Animation duration" append="ms">
						<b-input-group append="ms">
							<b-form-input type="number" min="0" max="5000" v-model="settings.duration"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Minimum duration">
						<b-input-group append="ms">
							<b-form-input type="number" min="0" max="60000" v-model.number="settings.alertDuration"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Linger duration" description="For the last alert">
						<b-input-group append="ms">
							<b-form-input type="number" min="0" max="60000" v-model="settings.linger"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Load timeout">
						<b-input-group append="ms">
							<b-form-input type="number" min="0" max="100000" step="1" v-model="settings.loadTimeout"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<text-settings :settings="settings" no-align />
		<b-card class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Text Color">
						<color-picker v-model="settings.textColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Gift Background">
						<color-picker v-model="settings.textBg" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Text outline blur" append="px">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="100" step="any" v-model="settings.blurRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Text outline size" append="px">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="10" step="any" v-model="settings.spreadRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col cols="6">
					<b-form-group label="Outline Color">
						<color-picker v-model="settings.shadowColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Width" append="px">
						<b-input-group append="px">
							<b-form-input type="number" min="0" step="any" v-model="settings.width"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Top margin" append="px">
						<b-input-group append="px">
							<b-form-input type="number" min="0" step="any" v-model="settings.marginTop"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Image height" append="px">
						<b-input-group append="px">
							<b-form-input type="number" min="0" step="any" v-model="settings.imageHeight"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card class="mb-3" title="Puppet">
			<b-form-row>
				<b-col cols="12" md="4">
					<b-form-group label="Puppet" description="Lip-syncs your TTS alerts as a puppet fixed to the bottom.">
						<b-form-select v-model="settings.puppet" :options="puppetOptions"></b-form-select>
					</b-form-group>
				</b-col>
				<b-col cols="6" md="4">
					<b-form-group label="Puppet size" append="px">
						<b-input-group append="px">
							<b-form-input type="number" min="80" max="1200" step="any" v-model.number="settings.puppetSize" :disabled="!settings.puppet"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col cols="6" md="4">
					<b-form-group label="Body shown" description="0.2 – 1.0">
						<b-form-input type="number" min="0.2" max="1" step="0.01" v-model.number="settings.puppetReveal" :disabled="!settings.puppet"></b-form-input>
					</b-form-group>
				</b-col>
			</b-form-row>
			<hr class="my-2" />
			<b-form-group label="Custom puppet (SVG)"
				:description="`Upload your own SVG (max ${puppetSvgMaxMB} MB) — it needs a group with id mouth holding the same viseme-* shapes as the template. Uploading one selects Custom automatically.`">
				<div class="mb-2">
					<a :href="`${$baseUrl}/lipsync/talking-frog.svg`" download="talking-frog-template.svg"><b-icon icon="download" class="mr-1" />Download the talking frog template</a>
				</div>
				<pipi-pupu-file-s3
					v-model="settings.puppetSvg"
					@input="onPuppetSvgInput"
					endpoint="puppetSvg"
					:validate="validatePuppetSvgFile"
					accept=".svg,image/svg+xml"
					placeholder="Choose or drop an SVG file…"
					preview
				></pipi-pupu-file-s3>
			</b-form-group>
		</b-card>

		<hide-channels v-model="settings.excludedChannels" :channels="channels" />
		<b-card class="mb-3">
			<b-form-group label="Extra CSS">
				<b-textarea v-model="settings.extraCSS"></b-textarea>
			</b-form-group>
		</b-card>
		</template>
		<template #preview="{ previewAudio }">
			<gift-widget-preview :settings="settings || {}" :speed="previewSpeed / 100" :preview-audio="previewAudio" />
		</template>
		<template #preview-options>
			<div class="d-flex align-items-center mt-2">
				<small class="text-muted preview-slider-label">Speed:</small>
				<b-form-input type="range" min="0" max="100" step="1" v-model.number="previewSpeed" class="mx-2 flex-grow-1" />
				<small class="text-muted preview-slider-value">{{ previewSpeedLabel }}</small>
			</div>
		</template>
	</widget-settings-layout>
</template>

<script>
import animateCssTransitions from '@/animateCssTransitions';
import ColorPicker from '@/components/ColorPicker.vue';
import WidgetSettingsLayout from '@/components/Widget/WidgetSettingsLayout.vue';
import TextSettings from '@/components/Widget/TextSettings.vue';
import GiftWidgetPreview from '@/components/Widget/GiftWidgetPreview.vue';
import VolumeSlider from '@/components/VolumeSlider.vue';
import HideChannels from '@/components/Widget/HideChannels.vue';
import PipiPupuFileS3 from '@/components/PipiPupuFileS3.vue';
import { validatePuppetSvg, MAX_PUPPET_SVG_BYTES } from '@/lipsync/puppetVisemes';

export default {
	beforeRouteLeave(to, from, next) {
		if (!this.hasUnsavedChanges) return next();
		this.$refs.layout.showLeaveModal(next, () => this.save());
	},
	data() { return {
		loading: 0,
		error: null,
		saving: 0,
		saveError: null,
		settings: null,
		channels: [],
		previewSpeed: 50,
		inTransitions: [ ...[{ text: 'Don\'t fade in', value: null }], ...animateCssTransitions.inTransitions ],
		outTransitions: [ ...[{ text: 'Don\'t fade out', value: null }], ...animateCssTransitions.outTransitions ],
		curColor: null,
		savedSettingsJson: null,
		giftEventTypes: null,
		user: null,
		queuePositionOptions: [ { text: 'Hidden', value: 'hidden' }, { text: 'Above', value: 'above' }, { text: 'Below', value: 'below' } ],
		puppetOptions: [ { text: 'None', value: '' }, { text: 'Talking frog', value: 'frog' }, { text: 'Custom (upload SVG)', value: 'custom' } ],
	}; },
	components: {
		ColorPicker,
		WidgetSettingsLayout,
		TextSettings,
		GiftWidgetPreview,
		VolumeSlider,
		HideChannels,
		PipiPupuFileS3,
	},
	async mounted() {
		window.addEventListener('beforeunload', this._onBeforeUnload);
		try {
			this.loading++;
			this.error = null;
			this.saveError = null;
			this.settings = null;
			const [settings, channels] = await Promise.all([
				this.$api.get('widgetSettings/gift'),
				this.$api.get('channels/widgets').catch(() => []),
			]);
			this.channels = channels;
			this.settings = {
				...settings,
				enableTTS: Boolean(settings.enableTTS),
				enableTTSProlog: Boolean(settings.enableTTSProlog),
				enableEventSounds: Boolean(settings.enableEventSounds),
				enableMedia: Boolean(settings.enableMedia),
				volume: settings.volume ?? 100,
				compressionAmount: settings.compressionAmount ?? 0,
				inTransition: settings.inTransition||null,
				outTransition: settings.outTransition||null,
				textColor: settings.textColor || 'rgba(255, 255, 255, 1)',
				shadowColor: settings.shadowColor || 'rgba(0, 0, 0, 1)',
				spreadRadius: settings.spreadRadius || 0,
				blurRadius: settings.blurRadius || 0,
				widgetBg: settings.widgetBg || 'rgba(0, 0, 0, 0)',
				fontFamily: settings.fontFamily || '',
				fontSize: settings.fontSize || 0,
				lineHeight: settings.lineHeight || 0,
				letterSpacing: settings.letterSpacing || 0,
				width: settings.width || 0,
				marginTop: settings.marginTop || 0,
				textAlign: 'center', // alert widget is always centered (no horizontal align setting)
				boldText: Boolean(settings.boldText),
				italicText: Boolean(settings.italicText),
				underlineText: Boolean(settings.underlineText),
				strikethroughText: Boolean(settings.strikethroughText),
				uppercaseText: Boolean(settings.uppercaseText),
				extraCSS: settings.extraCSS || '',
				customFont: Boolean(settings.customFont),
				minDonation: settings.minDonation || 0,
				ttsMinDonation: settings.ttsMinDonation || 0,
				loadQueue: Boolean(settings.loadQueue),
				queuePosition: settings.queuePosition || 'below',
				queueIconCount: settings.queueIconCount || 5,
				queueIconSize: settings.queueIconSize || 40,
				alertDuration: settings.alertDuration || 5000,
				excludedChannels: settings.excludedChannels || [],
				puppet: settings.puppet || '',
				puppetSize: settings.puppetSize || 360,
				puppetReveal: settings.puppetReveal || 0.67,
				puppetSvg: settings.puppetSvg || null,
			};
			this.savedSettingsJson = JSON.stringify(this.settings);
			await this.loadGiftEventTypes();
			this.user = await this.$api.get('userData');
		} catch(e) {
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	computed: {
		hasUnsavedChanges() {
			if(this.savedSettingsJson === null) return false;
			return JSON.stringify(this.settings) !== this.savedSettingsJson;
		},
		previewSpeedLabel() {
			if(this.previewSpeed < 33) return 'Slow';
			if(this.previewSpeed < 67) return 'Normal';
			return 'Fast';
		},
		puppetSvgMaxMB() {
			return Math.round(MAX_PUPPET_SVG_BYTES / 1024 / 1024);
		},
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this._onBeforeUnload);
	},
	methods: {
		_onBeforeUnload(e) {
			if (this.hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = '';
			}
		},
		async loadGiftEventTypes() {
			this.giftEventTypes = null;
			this.giftEventTypes = await Promise.all([
				this.$api.get('userEventType/gift'),
				this.$api.get('userEventType/subGift'),
				this.$api.get('userEventType/subscription'),
				this.$api.get('userEventType/subExtend'),
			]);
		},
		// Validate the viseme structure client-side (the server only checks size + safety).
		// Returns an error message, or null when the SVG is a valid puppet.
		async validatePuppetSvgFile(file) {
			if(file.size > MAX_PUPPET_SVG_BYTES) return `SVG must be ${this.puppetSvgMaxMB} MB or smaller.`;
			let text;
			try { text = await file.text(); }
			catch(e) { return 'Could not read the file.'; }
			const { ok, error } = validatePuppetSvg(text);
			return ok ? null : error;
		},
		// Uploading a custom SVG auto-selects Custom; removing it turns the puppet off.
		onPuppetSvgInput(val) {
			if(val) this.settings.puppet = 'custom';
			else if(this.settings.puppet === 'custom') this.settings.puppet = '';
		},
		async save() {
			try {
				this.saving++;
				this.error = null;

				await this.$api.post('widgetSettings/gift', { ...this.settings });
				this.savedSettingsJson = JSON.stringify(this.settings);
			} catch(err) {
				console.error('Error saving widget:', err);
				this.saveError = err;
			} finally {
				this.saving--;
			}
		},
	},
};
</script>
